import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import Card from '@/components/Card';
import { Button } from '../Button';
import { Save } from 'lucide-react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { useTranslation } from 'react-i18next';
import { EditContainer } from './styles';
import FormFieldRow from './FormFieldRow';

// Helper function to make fields with null values optional in the validation schema
const makeNullFieldsOptional = (
  schema: Yup.ObjectSchema<Record<string, unknown>>,
  fieldValues: NetworkInterfaceType
): Yup.ObjectSchema<Record<string, unknown>> => {
  if (!schema) return schema;

  try {
    // Get all fields from the schema description
    const schemaFields = schema.describe().fields;
    const newShape: Record<string, Yup.AnySchema> = {};

    Object.keys(schemaFields || {}).forEach((fieldKey) => {
      const fieldValue = fieldValues[fieldKey as keyof NetworkInterfaceType];

      // If the field value is null, make it optional and nullable
      if (fieldValue === null || fieldValue?.value === null) {
        newShape[fieldKey] = Yup.string().nullable().notRequired();
      } else {
        // For non-null fields, we need to reconstruct the schema based on field type
        // Use the existing field as-is with type casting to handle References
        const existingField = schema.fields[fieldKey];
        newShape[fieldKey] = existingField
          ? (existingField as unknown as Yup.AnySchema)
          : Yup.string();
      }
    });

    return Yup.object().shape(newShape);
  } catch {
    // If there's any error processing the schema, return it as-is
    return schema;
  }
};

// Modal state types
type WirelessModalState =
  | 'radio24'
  | 'radio50'
  | 'wifi24'
  | 'wifi50'
  | 'apSelect24'
  | 'apSelect50'
  | null;

// General modal states for WAN/LAN editing
type GeneralModalState =
  | 'selectWan'
  | 'selectLan'
  | 'editWan'
  | 'editLan'
  | false
  | null;

// Management modal states
type ManagementModalState = 'management' | 'selectWebUser' | 'webUser' | null;

// VoIP modal states
type VoipModalState = 'selectVoip' | 'editVoip' | null;

type AnyModalState =
  | WirelessModalState
  | GeneralModalState
  | ManagementModalState
  | VoipModalState;

// Type for network interface field values
interface ModalEditorProps<T extends AnyModalState> {
  title: string;
  fieldList: EditorPropType[];
  open: boolean;
  setOpen: Dispatch<SetStateAction<T>> | ((value: T) => void);
  validationSchema?: Yup.ObjectSchema<Record<string, unknown>>;
  handleSubmit: (values: Record<string, string | number>) => void;
  fieldValues?: NetworkInterfaceType;
  icon?: IconName;
  translatePath: string;
  externalOptions: {
    [key: string]: {
      options?: string[];
    };
  };
}

/** Displays a modal that allows parameters values edit of different type of network connections (WAN/LAN/RADIO) */
function ModalEditor<T extends AnyModalState>({
  open,
  setOpen,
  title,
  fieldList,
  validationSchema,
  fieldValues = {},
  handleSubmit,
  icon,
  translatePath,
  externalOptions,
}: ModalEditorProps<T>) {
  const { t } = useTranslation();

  // Helper function to parse comma-separated options from device values into an array of strings for select inputs
  const parseOptions = (rawValue: string | number | undefined): string[] => {
    if (!rawValue) return [];
    return String(rawValue)
      .split(',')
      .map((opt) => opt.trim())
      .filter((opt) => opt !== '');
  };

  /* Dynamically update field options based on fieldValues for specific fields 
  to assurance that the options displayed are supported by the device and provided 
  by the device*/
  const dynamicFieldList = useMemo(() => {
    return fieldList.map((originalField) => {
      const field = { ...originalField };

      if (field.field === 'channel') {
        const supported = fieldValues['channelsPossible']?.value;
        const newOptions = parseOptions(supported);
        if (newOptions.length > 0) {
          field.options = newOptions;
        }
      }

      if (field.field === 'transmitPower') {
        const supported = fieldValues['transmitPowerSupported']?.value;
        const newOptions = parseOptions(supported);
        if (newOptions.length > 0) {
          field.options = newOptions;
        }
      }

      if (field.field === 'bandWidthOperation') {
        const supported = fieldValues['bandWidthSupported']?.value;
        const newOptions = parseOptions(supported);
        if (newOptions.length > 0) {
          field.options = newOptions;
        }
      }

      return field;
    });
  }, [fieldList, fieldValues]);

  // Prepare initial values for Formik based on fieldList and fieldValues props getting current values on device
  const initialValues = Object.fromEntries(
    fieldList.map((field) => {
      const fieldData = fieldValues[field.field];
      const rawValue = fieldData?.value;
      return [
        field.field,
        String(
          rawValue ?? (field.type === 'boolean' ? 'false' : '')
        ),
      ];
    })
  );

  const handleClose = () => {
    setOpen(null as T);
  };

  // Generic schema in case schema it's not passed as prop
  const currentSchema = Yup.object(
    Object.fromEntries(fieldList.map((field) => [field.field, Yup.string()]))
  );

  // Apply null field optionality to validation schema
  const finalValidationSchema = useMemo(() => {
    const schemaToUse = validationSchema ?? currentSchema;
    return makeNullFieldsOptional(schemaToUse, fieldValues);
  }, [validationSchema, currentSchema, fieldValues]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={finalValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, submitForm, errors, touched }) => (
          <Card sx={{ maxWidth: '500px', gap: '24px' }}>
            <Stack direction="row" alignItems="center" gap="12px">
              <DynamicIcon name={icon ?? 'network'} />
              <Typography variant="h4" alignItems="center">
                {title}
              </Typography>
            </Stack>

            <EditContainer>
              {dynamicFieldList.map((field: EditorPropType) => {
                const visibilityKey = field.notVisible;
                const controllerValue = visibilityKey
                  ? values[String(visibilityKey)]
                  : null;
                const isVisible =
                  !visibilityKey || String(controllerValue) === 'true';
                if (fieldValues[String(field.field)] === null || !isVisible) {
                  return null;
                }
                return (
                  <FormFieldRow
                    key={field.field}
                    field={field}
                    fieldValues={fieldValues}
                    translatePath={translatePath}
                    externalOptions={externalOptions}
                  />
                );
              })}
            </EditContainer>

            <Box display="flex" justifyContent="flex-end" width="100%">
              <Button
                onClick={() => {
                  console.log('Form values:', values);
                  console.log('Form errors:', errors);
                  console.log('Form touched:', touched);
                  submitForm();
                }}
                variant="secondary"
                sx={{ gap: '8px' }}
              >
                <Save size={16} />
                {t('components.modalEditor.save')}
              </Button>
            </Box>
          </Card>
        )}
      </Formik>
    </Modal>
  );
}

export default ModalEditor;
